import React from 'react';
import { Buildings } from '@phosphor-icons/react';

interface CustomerRow {
    customerName: string;
    customerAccountUuid: string;
    totalEngagements: number;
    activeEngagements: number;
    completedEngagements: number;
    avgProgress: number;
    totalCoachees: number;
    totalCoaches: number;
}

interface CustomerCoachingOverviewWidgetProps {
    data?: CustomerRow[] | null;
    loading?: boolean;
    error?: string;
}

const MiniRing: React.FC<{ pct: number; color: string }> = ({ pct, color }) => (
    <svg width={48} height={48} viewBox="0 0 36 36" className="-rotate-90">
        <circle cx="18" cy="18" r="14" fill="none" stroke="#f3f4f6" strokeWidth="3" />
        <circle cx="18" cy="18" r="14" fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${pct * 0.88} 88`} strokeLinecap="round" />
    </svg>
);

export const CustomerCoachingOverviewWidget: React.FC<CustomerCoachingOverviewWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    return (
        <div className="flex flex-col h-full p-5">
            <div className="flex-1">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Buildings className="w-8 h-8" />
                        <span className="text-sm">No customer accounts found</span>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-3 overflow-auto h-full content-start">
                        {rows.map((row, idx) => {
                            const progress = Number(row.avgProgress) || 0;
                            const active = Number(row.activeEngagements) || 0;
                            const completed = Number(row.completedEngagements) || 0;
                            const total = Number(row.totalEngagements) || 0;
                            const coachees = Number(row.totalCoachees) || 0;
                            const coaches = Number(row.totalCoaches) || 0;
                            const hasActivity = total > 0;

                            const ringColor = progress >= 70 ? '#10B981' : progress >= 40 ? '#F59E0B' : '#EF4444';
                            const pctClass = progress >= 70 ? 'text-emerald-600' : progress >= 40 ? 'text-amber-600' : 'text-red-500';

                            return (
                                <div key={row.customerAccountUuid || idx}
                                    className={`rounded-xl border p-3.5 ${hasActivity ? 'border-gray-200' : 'border-dashed border-gray-200 opacity-50'}`}>

                                    {/* Header: name + ring */}
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="relative shrink-0">
                                            <MiniRing pct={progress} color={ringColor} />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className={`text-[10px] font-bold ${pctClass}`}>{progress}%</span>
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0 pt-1">
                                            <div className="text-[13px] font-semibold text-gray-800 truncate">{row.customerName || '\u2014'}</div>
                                            <div className="text-[10px] text-gray-400 mt-0.5">{total} engagement{total !== 1 ? 's' : ''}</div>
                                        </div>
                                    </div>

                                    {/* Stat grid */}
                                    <div className="grid grid-cols-2 gap-1.5">
                                        <div className="bg-blue-50 rounded-md py-1.5 px-2 text-center">
                                            <div className="text-sm font-bold text-blue-600">{active}</div>
                                            <div className="text-[9px] text-gray-400">Active</div>
                                        </div>
                                        <div className="bg-emerald-50 rounded-md py-1.5 px-2 text-center">
                                            <div className="text-sm font-bold text-emerald-600">{completed}</div>
                                            <div className="text-[9px] text-gray-400">Completed</div>
                                        </div>
                                        <div className="bg-purple-50 rounded-md py-1.5 px-2 text-center">
                                            <div className="text-sm font-bold text-purple-600">{coachees}</div>
                                            <div className="text-[9px] text-gray-400">Coachees</div>
                                        </div>
                                        <div className="bg-amber-50 rounded-md py-1.5 px-2 text-center">
                                            <div className="text-sm font-bold text-amber-600">{coaches}</div>
                                            <div className="text-[9px] text-gray-400">Coaches</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
